import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Store } from '@ngxs/store';
import { LicenseTierDropdown } from '../../interface/license-tiers';
import { EnterpriseLicenseActions } from 'src/app/state/enterprise-license/enterprise-license.actions';

@Component({
  selector: 'app-add-edit-modal',
  templateUrl: './add-edit-modal.component.html',
  styleUrls: ['./add-edit-modal.component.scss'],
})
export class AddEditModalComponent implements OnInit {
  form: FormGroup;
  model: any = {};
  options: FormlyFormOptions = {};

  licenseFields: FormlyFieldConfig[] = [];
  licenseTiers: LicenseTierDropdown[] = [];
  currentTierName: string = '';
  originalModel: any = {};

  mode: 'add' | 'edit' = 'add';

  constructor(
    private readonly fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly store: Store
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {

    const { mode, licenseData, licenseTiers } = this.config.data;
    this.mode = mode;
    this.licenseTiers = licenseTiers ?? [];

    if (mode === 'edit' && licenseData) {
      // Make sure tier value matches one of the loaded licenseTierId GUIDs
      const matchedTier = this.licenseTiers.find(
        (tier) =>
          tier.licenseTierId === licenseData.licenseTierId ||
          tier.name === licenseData.name
      );

      this.model = {
        ...licenseData,
        licenseTierId: String(matchedTier?.licenseTierId ?? licenseData.licenseTierId),
        licenseNumber: licenseData.licenseNumber,
      };
      this.currentTierName = matchedTier?.name ?? '';
    }
    this.initializeFields();
     this.originalModel = JSON.parse(JSON.stringify(this.model));
  }

  initializeFields(): void {
    this.licenseFields = [
      {
        fieldGroupClassName: 'p-fluid p-formgrid p-grid',
        fieldGroup: [
          // Left Column Fields
          {
            className: 'p-col-12 p-md-6',
            key: 'licenseName',
            type: 'input',
            templateOptions: {
              label: 'License Name',
              required: true,
              placeholder: 'License Name',
              minLength: 3,
              maxLength: 255,
              pattern: /^[a-zA-Z0-9 ]+$/,
            },
            validation: {
              messages: {
                required: 'License Name is required',
                minlength: 'Minimum 3 characters',
                maxlength: 'Maximum 255 characters',
                pattern: 'Only letters and numbers allowed',
              },
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'clientName',
            type: 'input',
            templateOptions: {
              label: 'Client Name',
              required: true,
              placeholder: 'Client Name',
              minLength: 3,
              maxLength: 255,
              pattern: /^[a-zA-Z0-9 ]+$/,
            },
            validation: {
              messages: {
                required: 'Client Name is required',
                minlength: 'Minimum 3 characters',
                maxlength: 'Maximum 255 characters',
                pattern: 'Only letters and numbers allowed',
              },
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'projectLocation',
            type: 'input',
            templateOptions: {
              label: 'Project Location',
              required: true,
              placeholder: 'Project Location',
              maxLength: 255,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 255 characters',
              },
            },
          },
          {
            key: 'licenseTierId',
            type: 'select',
            className: 'p-col-12 p-md-6',
            props: {
              label: 'Tier',
              required: true,
              placeholder: 'Select Tier',
              options: this.licenseTiers.map(tier => ({
                label: tier.name,
                value: tier.licenseTierId,
                disabled: this.shouldDisableTierOption(tier),
              })),
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'oracleProjectCode',
            type: 'input',
            templateOptions: {
              label: 'Oracle Project Code',
              required: true,
              placeholder: 'Enter Oracle Project Code',
              maxLength: 255,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 255 characters',
              },
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'oracleTaskCode',
            type: 'input',
            templateOptions: {
              label: 'Oracle Task Code',
              required: true,
              placeholder: 'Enter Oracle Task Code',
              maxLength: 255,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 255 characters',
              },
            },
          },

          // Notes (Full Width)
          {
            className: 'p-col-12',
            key: 'notes',
            type: 'textarea',
            templateOptions: {
              label: 'Notes',
              rows: 3,
              placeholder: 'Add Note',
              maxLength: 500,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 500 characters',
              },
            },
          },
        ],
      },
    ];
  }

  shouldDisableTierOption(tier: LicenseTierDropdown): boolean {
    const tier1 = 'Tier 1';
    const tier2 = 'Tier 2';
    const tier3 = 'Tier 3';

    // Only disable options in edit mode
    if (this.mode !== 'edit') {
      return false;
    }

    const currentTier = this.currentTierName?.trim();
    if (!currentTier) return false;

    switch (currentTier) {
      case tier2:
        // If current is Tier 2 → disable Tier 1
        return tier.name === tier1;

      case tier3:
        // If current is Tier 3 → disable Tier 1 and Tier 2
        return tier.name === tier1 || tier.name === tier2;

      default:
        // If current is Tier 1 → allow all changes
        return false;
    }
  }

  onSave(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const license = {
        ...formValue,
        licenseNumber: this.model.licenseNumber,
      };

      const isAddMode = this.mode === 'add';

      const action = isAddMode
        ? new EnterpriseLicenseActions.CreateLicense(license, {
          success: 'License created successfully',
          error: 'Failed to create license',
        })
        : new EnterpriseLicenseActions.UpdateLicense(this.model.id, license, {
          success: 'License updated successfully',
          error: 'Failed to update license',
        });

      this.store.dispatch(action).subscribe({
        next: () => {
          console.log(isAddMode ? 'License created' : 'License updated');
          if (this.mode == 'edit') {
            this.ref.close(action);
          }
          this.ref.close(true); // ✅ Close modal after successful save
        },
        error: (err) => {
          console.error('Save failed:', err);
          this.ref.close(false);
        }
      });

    } else {
      this.form.markAllAsTouched(); // Show validation errors
    }
  }

  get hasChanges(): boolean {
    return JSON.stringify(this.model) !== JSON.stringify(this.originalModel);
  }

  onCancel(): void {
    this.ref.close(); // ✅ close without data
  }
}
